/**
 * @file 本だなの区画を、選んでいる人の計画から決める。
 *
 * @what: 学ぶ人が進めている計画が指すカリキュラムに載っている本の ISBN を集める。本だなの資料は、
 *   その ISBN に当たれば「取り組むもの」、当たらなければ「教養」の区画へ入る。
 *   「計画のもと」と、web がまだ知らない役割の資料は、取り込んだときの役割のまま残る。
 * @why: 同じ本でも、中学入試を目指す人にとっての大学入試の参考書は取り組む対象ではない (代表の判断、
 *   2026-09-11)。取り込んだときに付けた役割は家族で1つしか持てないので、人ごとの見え方は計画から出す。
 * @failure: 副作用を持たない純関数である。計画がまだ無いとき、計画が指すカリキュラムのルートが無いときは
 *   空を返し、本だなは「教養」だけになる。突き合わせは ISBN で行うので、ISBN の無い資料は当たらない。
 */

import type { StudyLearningPlan } from "../api/learningPlans";
import type { StudySource, StudySourceKind } from "../api/sources";
import { latestTextbookRoutes, type StudyTextbookRoute } from "../api/textbookRoutes";
import { mergeRoutesByTarget } from "./textbookRouteGrid";

/**
 * 計画で入れ替える区画を並べる。
 *
 * @why: 「計画のもと」は計画を立てるときに読むもので、取り組む本かどうかとは別の軸である。web がまだ
 *   知らない役割 (backend だけ先に増えた場合) も同じ理由でそのまま残し、棚から消えないようにする。
 */
const REASSIGNED_KINDS: readonly StudySourceKind[] = ["material", "culture"];

/**
 * 計画が指すカリキュラムに載っている本の ISBN を集める。
 *
 * @what: 進めている計画すべてについて、その計画が指すカリキュラムのルートを1枚に重ね、計画で使う科目の
 *   本の ISBN を返す。本人が使わないと決めたもの (使わない区分・目標と違う志望校の過去問) は返さない。
 *   ISBN を持たない本も返さない。終えた計画は数えない。
 * @why: カリキュラムに載っている本は、いま手を付ける順番でなくても取り組む対象である (代表の判断、
 *   2026-09-13)。スケジュールに入った本だけに絞ると、代替・補助の本や、目標の偏差値の外にある本が
 *   「教養」へ落ちてしまう。本人が設定で外したもの (使わない科目・使わない区分・志望校) だけを除く。
 *   ※なぜなら計画は2〜3件を並行して持てるので、どれか1つのカリキュラムに載っていれば取り組む本だ
 *   からである。
 * @failure: 偏差値と役割 (推奨・代替・上位・補助) は見ない。スケジュールの並びとは一致しない。
 */
export function plannedBookIsbns(
  routes: readonly StudyTextbookRoute[],
  plans: readonly StudyLearningPlan[],
): Set<string> {
  const out = new Set<string>();
  const latest = latestTextbookRoutes(routes);
  for (const plan of plans) {
    if (plan.status !== "active") continue;
    // 計画はカリキュラムの系列を指すので、同じ系列のルートだけを重ねる。
    const mine = latest.filter((r) => r.series_id === plan.route_series_id);
    const merged = mergeRoutesByTarget(mine)[0];
    if (merged === undefined) continue;
    const goalSchool = plan.goal_school && plan.goal_school.length > 0 ? plan.goal_school : null;
    for (const subject of merged.subjects) {
      const setting = plan.subjects[subject.id];
      if (!setting || !setting.enabled) continue;
      const excluded = new Set(setting.excluded_tracks ?? []);
      for (const gb of subject.books) {
        // 使わない区分 (設定で外した塾の教材・銀本など) の本は取り組む対象にしない。
        if (excluded.has(gb.trackId)) continue;
        // 特定の志望校のための本 (過去問) は、目標の志望校が決まっていればその校のものだけを数える。
        if (goalSchool !== null && gb.book.school && gb.book.school !== goalSchool) continue;
        const isbn = gb.book.isbn;
        if (isbn) out.add(isbn);
      }
    }
  }
  return out;
}

/**
 * 資料1点を置く区画を返す。
 *
 * @what: planned が null (まなぶ人が未定・計画を読めなかった) のときは取り込んだときの役割をそのまま
 *   返す。そうでなければ、教材と教養の資料だけを計画で入れ替える。
 */
export function shelfSectionOf(
  source: StudySource,
  planned: ReadonlySet<string> | null,
): StudySourceKind {
  if (planned === null || !REASSIGNED_KINDS.includes(source.kind)) return source.kind;
  return source.isbn !== undefined && planned.has(source.isbn) ? "material" : "culture";
}
